import { useNavigate } from 'react-router-dom'
import { Organization } from '../api'
import { DataTable, ColumnDef } from '@/components/shared/DataTable/DataTable'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Building2, Eye, Users, Layers } from 'lucide-react'

interface OrganizationTableProps {
  organizations: Organization[]
  isLoading: boolean
}

export function OrganizationTable({ organizations, isLoading }: OrganizationTableProps) {
  const navigate = useNavigate()

  // Define table columns
  const columns: ColumnDef<Organization>[] = [
    {
      key: 'organization',
      header: 'Organization',
      cell: (org) => (
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/organizations/${org._id}`)
          }}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
            <Building2 className="h-4 w-4" />
          </div>
          <div>
            <div className="font-medium">{org.displayName}</div>
            <div className="text-sm text-muted-foreground">{org.name}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'tenant',
      header: 'Tenant',
      cell: (org) => (
        <div className="text-sm">
          {org.tenantName ? (
            <div className="flex items-center gap-1">
              <Layers className="h-3 w-3 text-muted-foreground" />
              <span>{org.tenantName}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'domain',
      header: 'Domain',
      cell: (org) => (
        <div className="text-sm">
          {org.domain ? (
            <span className="font-mono">{org.domain}</span>
          ) : (
            <span className="text-muted-foreground">No domain</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (org) => (
        <div className="flex items-center gap-2">
          <Badge variant={org.isActive ? 'default' : 'secondary'}>
            {org.isActive ? 'Active' : 'Inactive'}
          </Badge>
          {org.name === 'default' && (
            <Badge variant="outline" className="text-xs">
              Default
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'users',
      header: 'Users',
      headerClassName: 'text-center',
      className: 'text-center',
      cell: (org) => (
        <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          {org.userCount || 0}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      cell: (org) => (
        <div className="text-sm text-muted-foreground">
          {new Date(org.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (org) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/organizations/${org._id}`)}
          >
            <Eye className="mr-1 h-4 w-4" />
            Details
          </Button>
        </div>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={organizations}
      isLoading={isLoading}
      emptyMessage="No organizations found"
      emptyIcon={<Building2 className="h-12 w-12 text-muted-foreground" />}
      onRowClick={(org) => navigate(`/organizations/${org._id}`)}
    />
  )
}
